import { Reservation, ServiceOrderState } from '@mews-commander/data-access';
import {
    LocalizedDateFromUtc,
    LocalizedDateTimeFromUtc,
    LocalizedShortDayNameFromUtc,
    LocalizedShortTimeFromUtc,
    SpacedComma,
    SpacedDash,
    TooltippedSpan,
} from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { Card, CardContent, Divider, Stack, Tag, Typography } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, lt } from '@mews/framework';
import { isUnconfirmedState } from '@mews-commander/types';
import {
    getServiceTimeUnitPluralKey,
    getServiceTimeUnitSingularKey,
} from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { getReservationsDefaultProductOrderItems } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { useCommanderSelector } from '@mews-commander/utils';
import { ResourceStateTag } from '../../../../../components';
import { ReservationStateName } from '../../reservationListItem/ReservationStateName';
import SkipCleaningDoorHanger from '../../SkipCleaningDoorHanger';
import { NotesList } from '../NotesList';
import { UnassignedResourceTag } from '../../UnassignedResourceTag';
import { useIsDigitalKeyStatus } from '../../../utils/useIsDigitalKeyStatus';
import ResourceContent from './ResourceContent';

interface BasicReservationInfoCardProps {
    reservation: Reservation;
}

const BasicReservationInfoCard = ({
    reservation: {
        id,
        startUtc,
        startedUtc,
        endUtc,
        releasedUtc,
        reservationTimeState,
        serviceOrderState,
        options,
        serviceId,
        number,
        assignedResourceId,
        requestedCategoryId,
    },
}: BasicReservationInfoCardProps) => {
    const timeUnitCount = useCommanderSelector((state) =>
        getReservationsDefaultProductOrderItems(state, {
            serviceId,
            serviceOrderIds: [id],
        })
    ).length;

    const timeUnitKey = useCommanderSelector((state) =>
        timeUnitCount > 1
            ? getServiceTimeUnitPluralKey(state, { id: serviceId })
            : getServiceTimeUnitSingularKey(state, { id: serviceId })
    );

    const isDigitalKeyReady = useIsDigitalKeyStatus(id);
    return (
        <Card overflowHidden padding="0" data-test-smart-detail-card="basic-info">
            <CardContent padding="200" data-test-smart-detail-card-subpart="main-info">
                <Stack spacing="50" vertical>
                    <Stack distribution="space-between">
                        <Typography textStyle="bodyMediumStrong" data-test-smart-detail-reservation-interval as="div">
                            <LocalizedDateFromUtc utcString={startedUtc || startUtc} />
                            {SpacedDash}
                            <LocalizedDateFromUtc utcString={endUtc} />
                        </Typography>
                        <ReservationStateName
                            reservationTimeState={reservationTimeState}
                            serviceOrderState={serviceOrderState}
                            options={options}
                        />
                    </Stack>

                    <Stack vertical>
                        <Stack distribution="space-between">
                            {isUnconfirmedState(serviceOrderState) && !!releasedUtc ? (
                                <Typography
                                    textStyle="bodySmallStrong"
                                    variant="secondary"
                                    data-test-smart-detail-release-date
                                    as="div"
                                >
                                    <LocalizedText textKey={TextKey.Release} />
                                    {UnicodeEntity.NoBreakSpace}
                                    <LocalizedDateTimeFromUtc utcString={releasedUtc} />
                                </Typography>
                            ) : null}

                            <Typography
                                textStyle="bodySmallStrong"
                                variant="secondary"
                                data-test-smart-detail-reservation-duration
                                as="div"
                            >
                                {timeUnitCount}
                                {UnicodeEntity.NoBreakSpace}
                                {timeUnitKey && <LocalizedText textKey={timeUnitKey} firstLowerCase />}
                                {SpacedComma}
                                <LocalizedShortDayNameFromUtc utcString={startedUtc || startUtc} />
                                {UnicodeEntity.NoBreakSpace}
                                <LocalizedShortTimeFromUtc utcString={startedUtc || startUtc} />
                                {SpacedDash}
                                <LocalizedShortDayNameFromUtc utcString={endUtc} />
                                {UnicodeEntity.NoBreakSpace}
                                <LocalizedShortTimeFromUtc utcString={endUtc} />
                            </Typography>
                            {isDigitalKeyReady && <Tag variant="lavender">{lt('DigitalKeyReady')}</Tag>}
                        </Stack>
                    </Stack>
                </Stack>
                <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                    <TooltippedSpan
                        tooltip={<LocalizedText textKey={TextKey.ConfirmationNumber} />}
                        data-test-smart-detail-confirmation-number
                    >
                        {number}
                    </TooltippedSpan>
                </Typography>
            </CardContent>
            <Divider />
            <CardContent padding="200" data-test-smart-detail-card-subpart="resource-info">
                <Stack distribution="space-between" itemAlignment="center">
                    <Typography textStyle="bodyMediumStrong" as="div">
                        <ResourceContent
                            assignedResourceId={assignedResourceId}
                            requestedCategoryId={requestedCategoryId}
                            serviceId={serviceId}
                            id={id}
                        />
                    </Typography>
                    <Stack spacing="100" itemAlignment="center">
                        <SkipCleaningDoorHanger reservationId={id} />
                        {assignedResourceId === null && serviceOrderState !== ServiceOrderState.Canceled ? (
                            <UnassignedResourceTag />
                        ) : null}
                        {assignedResourceId !== null ? <ResourceStateTag resourceId={assignedResourceId} /> : null}
                    </Stack>
                </Stack>
            </CardContent>
            <NotesList serviceOrderId={id} />
        </Card>
    );
};

export default BasicReservationInfoCard;
